import React, { useEffect, useState } from "react";
import { api, apiErr, API } from "../../lib/api";
import { toast } from "sonner";
import { ShieldCheck, Plus, Trash2, FileDown, X, Download, User } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../../components/ui/dialog";

const EMPTY_COMP = { nom: "", prenoms: "", date_naissance: "", lieu_naissance: "", nationalite: "", mineur: false };
const fmt = (s) => { if (!s) return "—"; const [y, m, d] = String(s).slice(0, 10).split("-"); return d ? `${d}-${m}-${y}` : s; };

export default function PoliceTab() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialog, setDialog] = useState(false);

  const load = () => {
    setLoading(true);
    api.get("/admin/police").then((r) => setRows(r.data)).catch((e) => toast.error(apiErr(e))).finally(() => setLoading(false));
  };
  useEffect(load, []);

  const del = async (id) => {
    if (!window.confirm("Supprimer définitivement cette fiche de police ?")) return;
    try { await api.delete(`/admin/police/${id}`); toast.success("Fiche supprimée"); load(); }
    catch (e) { toast.error(apiErr(e)); }
  };

  const downloadPdf = async (id, nom) => {
    try {
      const r = await api.get(`/admin/police/${id}/pdf`, { responseType: "blob" });
      const url = URL.createObjectURL(r.data);
      const a = document.createElement("a");
      a.href = url; a.download = `fiche-police-${(nom || id).replace(/\s+/g, "-")}.pdf`;
      document.body.appendChild(a); a.click(); a.remove(); URL.revokeObjectURL(url);
    } catch (e) { toast.error(apiErr(e)); }
  };

  const exportCsv = () => {
    const head = ["Nom", "Prénoms", "Naissance", "Lieu naissance", "Nationalité", "Domicile", "E-mail", "Téléphone", "Hébergement", "Arrivée", "Départ", "Accompagnants", "Rempli par", "Date"];
    const lines = rows.map((r) => [
      r.nom, r.prenoms, r.date_naissance, r.lieu_naissance, r.nationalite, r.domicile, r.email, r.telephone,
      r.accommodation_name, r.arrivee, r.depart, (r.accompagnants || []).length, r.filled_by, (r.created_at || "").slice(0, 10),
    ].map((v) => `"${String(v ?? "").replace(/"/g, '""')}"`).join(","));
    const csv = [head.join(","), ...lines].join("\n");
    const blob = new Blob(["\uFEFF" + csv], { type: "text/csv;charset=utf-8;" });
    const a = document.createElement("a"); a.href = URL.createObjectURL(blob); a.download = "fiches-police.csv"; a.click();
  };

  const publicLink = `${window.location.origin}/fiche-police`;

  return (
    <div data-testid="police-tab">
      <div className="flex flex-wrap items-center gap-3 mb-4">
        <div>
          <h2 className="text-lg font-semibold text-slate-900 flex items-center gap-2"><ShieldCheck size={20} className="text-brand" /> Fiches de police</h2>
          <p className="text-xs text-ink-400 mt-0.5">Voyageurs étrangers (art. R.611-42 CESEDA). Conservation légale 6 mois puis suppression automatique.</p>
        </div>
        <div className="ml-auto flex flex-wrap gap-2">
          <button onClick={() => navigator.clipboard.writeText(publicLink).then(() => toast.success("Lien copié"))} className="rounded-full border border-slate-200 text-slate-700 px-4 py-2 text-sm font-medium hover:bg-slate-50" data-testid="police-copy-link">Copier le lien voyageur</button>
          <button onClick={exportCsv} disabled={!rows.length} className="inline-flex items-center gap-1.5 rounded-full border border-slate-200 text-slate-700 px-4 py-2 text-sm font-medium hover:bg-slate-50 disabled:opacity-50" data-testid="police-export-csv"><Download size={14} /> Export CSV</button>
          <button onClick={() => setDialog(true)} className="inline-flex items-center gap-1.5 rounded-full bg-brand text-white px-4 py-2 text-sm font-medium hover:bg-brand-600" data-testid="police-new"><Plus size={15} /> Nouvelle fiche</button>
        </div>
      </div>

      {loading ? <p className="text-ink-400">Chargement…</p> : !rows.length ? (
        <div className="rounded-xl border border-slate-200 bg-white p-10 text-center text-ink-400">
          <ShieldCheck size={32} className="mx-auto mb-3 text-slate-300" />
          Aucune fiche pour le moment. Partagez le lien voyageur ou ajoutez une fiche manuellement.
        </div>
      ) : (
        <div className="overflow-x-auto rounded-xl border border-slate-200 bg-white shadow-sm">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-xs text-ink-400 border-b border-slate-100">
                <th className="p-3">Voyageur</th><th className="p-3">Nationalité</th><th className="p-3">Séjour</th>
                <th className="p-3">Hébergement</th><th className="p-3">Accomp.</th><th className="p-3">Source</th><th className="p-3 text-right">Actions</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r) => (
                <tr key={r.id} className="border-b border-slate-50 hover:bg-slate-50/60" data-testid={`police-row-${r.id}`}>
                  <td className="p-3 font-medium text-ink-900">{r.nom} {r.prenoms}<span className="block text-[11px] text-ink-400 font-normal">Né(e) le {fmt(r.date_naissance)}</span></td>
                  <td className="p-3 text-ink-700">{r.nationalite || "—"}</td>
                  <td className="p-3 text-ink-600 whitespace-nowrap">{fmt(r.arrivee)} → {fmt(r.depart)}</td>
                  <td className="p-3 text-ink-600">{r.accommodation_name || "—"}</td>
                  <td className="p-3 text-ink-600">{(r.accompagnants || []).length}</td>
                  <td className="p-3"><span className={`text-[11px] rounded-full px-2 py-0.5 ${r.filled_by === "guest" ? "bg-emerald-50 text-emerald-700" : "bg-brand-50 text-brand"}`}>{r.filled_by === "guest" ? "Voyageur" : "Admin"}</span></td>
                  <td className="p-3">
                    <div className="flex justify-end gap-2">
                      <button onClick={() => downloadPdf(r.id, r.nom)} className="p-1.5 rounded-lg border border-slate-200 text-brand hover:bg-brand-50" title="Télécharger le PDF" data-testid={`police-pdf-${r.id}`}><FileDown size={15} /></button>
                      <button onClick={() => del(r.id)} className="p-1.5 rounded-lg border border-slate-200 text-red-500 hover:bg-red-50" title="Supprimer"><Trash2 size={15} /></button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <PoliceDialog open={dialog} onClose={() => setDialog(false)} onSaved={() => { setDialog(false); load(); }} />
    </div>
  );
}

function PoliceDialog({ open, onClose, onSaved }) {
  const [form, setForm] = useState({ nom: "", prenoms: "", date_naissance: "", lieu_naissance: "", nationalite: "", domicile: "", email: "", telephone: "", arrivee: "", depart: "", accompagnants: [] });
  const [saving, setSaving] = useState(false);
  useEffect(() => { if (open) setForm({ nom: "", prenoms: "", date_naissance: "", lieu_naissance: "", nationalite: "", domicile: "", email: "", telephone: "", arrivee: "", depart: "", accompagnants: [] }); }, [open]);

  const s = (k) => (e) => setForm({ ...form, [k]: e.target.value });
  const addComp = () => setForm({ ...form, accompagnants: [...form.accompagnants, { ...EMPTY_COMP }] });
  const rmComp = (i) => setForm({ ...form, accompagnants: form.accompagnants.filter((_, x) => x !== i) });
  const setComp = (i, k, v) => setForm({ ...form, accompagnants: form.accompagnants.map((c, x) => x === i ? { ...c, [k]: v } : c) });

  const save = async () => {
    if (!form.nom || !form.prenoms || !form.date_naissance || !form.nationalite) return toast.error("Nom, prénom(s), date de naissance et nationalité requis.");
    setSaving(true);
    try { await api.post("/admin/police", form); toast.success("Fiche enregistrée"); onSaved(); }
    catch (e) { toast.error(apiErr(e)); } finally { setSaving(false); }
  };

  const input = "w-full rounded-lg border border-ink-400/25 px-3 py-2 text-sm outline-none focus:border-brand";
  const label = "text-xs font-medium text-ink-600 mb-1 block";

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="bg-cream max-w-lg max-h-[85vh] overflow-y-auto" data-testid="police-dialog">
        <DialogHeader><DialogTitle className="font-heading text-2xl">Nouvelle fiche de police</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <div className="grid grid-cols-2 gap-3">
            <div><label className={label}>Nom *</label><input className={input} value={form.nom} onChange={s("nom")} data-testid="police-dialog-nom" /></div>
            <div><label className={label}>Prénom(s) *</label><input className={input} value={form.prenoms} onChange={s("prenoms")} /></div>
            <div><label className={label}>Date de naissance *</label><input type="date" className={input} value={form.date_naissance} onChange={s("date_naissance")} /></div>
            <div><label className={label}>Lieu de naissance</label><input className={input} value={form.lieu_naissance} onChange={s("lieu_naissance")} /></div>
            <div><label className={label}>Nationalité *</label><input className={input} value={form.nationalite} onChange={s("nationalite")} /></div>
            <div><label className={label}>Domicile</label><input className={input} value={form.domicile} onChange={s("domicile")} /></div>
            <div><label className={label}>E-mail</label><input className={input} value={form.email} onChange={s("email")} /></div>
            <div><label className={label}>Téléphone</label><input className={input} value={form.telephone} onChange={s("telephone")} /></div>
            <div><label className={label}>Arrivée</label><input type="date" className={input} value={form.arrivee} onChange={s("arrivee")} /></div>
            <div><label className={label}>Départ</label><input type="date" className={input} value={form.depart} onChange={s("depart")} /></div>
          </div>
          <div className="pt-1">
            <div className="flex items-center justify-between mb-1">
              <span className="text-xs font-medium text-ink-600">Accompagnants (dont mineurs)</span>
              <button type="button" onClick={addComp} className="text-xs text-brand font-medium inline-flex items-center gap-1"><Plus size={13} /> Ajouter</button>
            </div>
            <div className="space-y-2">
              {form.accompagnants.map((c, i) => (
                <div key={i} className="rounded-lg bg-white border border-black/5 p-3">
                  <div className="flex justify-between items-center mb-2"><span className="text-[11px] text-ink-400">#{i + 1}</span><button type="button" onClick={() => rmComp(i)} className="text-ink-400 hover:text-red-500"><Trash2 size={13} /></button></div>
                  <div className="grid grid-cols-2 gap-2">
                    <input className={input} placeholder="Nom" value={c.nom} onChange={(e) => setComp(i, "nom", e.target.value)} />
                    <input className={input} placeholder="Prénom(s)" value={c.prenoms} onChange={(e) => setComp(i, "prenoms", e.target.value)} />
                    <input type="date" className={input} value={c.date_naissance} onChange={(e) => setComp(i, "date_naissance", e.target.value)} />
                    <input className={input} placeholder="Nationalité" value={c.nationalite} onChange={(e) => setComp(i, "nationalite", e.target.value)} />
                    <label className="flex items-center gap-2 text-xs text-ink-600 col-span-2"><input type="checkbox" checked={c.mineur} onChange={(e) => setComp(i, "mineur", e.target.checked)} /> Mineur</label>
                  </div>
                </div>
              ))}
            </div>
          </div>
          <div className="flex gap-3 pt-1">
            <button onClick={save} disabled={saving} className="flex-1 rounded-full bg-brand text-white py-2.5 font-medium hover:bg-brand-600 disabled:opacity-60" data-testid="police-dialog-save">{saving ? "Enregistrement…" : "Enregistrer"}</button>
            <button onClick={onClose} className="rounded-full border border-ink-400/30 text-ink-600 px-6 py-2.5"><X size={16} /></button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
